import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PmsStatusBadge } from '@/components/pms/PmsStatusBadge';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function RequestsTab({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(null);

  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 30000);
    return () => clearInterval(interval);
  }, [hotelId]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/service-requests/${hotelId}`);
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>
    );
  }

  let filteredRequests = requests;
  if (filter === 'pending') {
    filteredRequests = requests.filter(r => ['pending', 'acknowledged', 'in_progress'].includes(r.status));
  } else if (filter === 'overdue') {
    filteredRequests = requests.filter(r => r.escalated_to_manager);
  }
  if (categoryFilter) {
    filteredRequests = filteredRequests.filter(r => r.category === categoryFilter);
  }

  const pendingCount = requests.filter(r => ['pending', 'acknowledged', 'in_progress'].includes(r.status)).length;
  const overdueCount = requests.filter(r => r.escalated_to_manager).length;
  const completedCount = requests.filter(r => r.status === 'completed').length;

  return (
    <div className="space-y-6" data-testid="requests-monitor-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-2">Service Requests</h2>
          <p className="text-muted-foreground">Monitor and manage all guest service requests</p>
        </div>
        <Button onClick={loadRequests} variant="outline" size="sm" data-testid="requests-refresh-btn">
          <ArrowRight className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('pending')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{pendingCount}</p>
            <p className="text-sm text-muted-foreground">Active Requests</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-destructive/20" onClick={() => setFilter('overdue')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{overdueCount}</p>
            <p className="text-sm text-muted-foreground">Overdue / Escalated</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[hsl(var(--status-clean))]">{completedCount}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Badge variant={filter === 'all' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setFilter('all')}>
          All ({requests.length})
        </Badge>
        <Badge variant={filter === 'pending' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setFilter('pending')}>
          Pending ({pendingCount})
        </Badge>
        <Badge variant={filter === 'overdue' ? 'destructive' : 'outline'} className="cursor-pointer" onClick={() => setFilter('overdue')}>
          Overdue ({overdueCount})
        </Badge>
        <div className="mx-2">|</div>
        <Badge variant={categoryFilter === 'housekeeping' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setCategoryFilter(categoryFilter === 'housekeeping' ? null : 'housekeeping')}>
          Housekeeping
        </Badge>
        <Badge variant={categoryFilter === 'maintenance' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setCategoryFilter(categoryFilter === 'maintenance' ? null : 'maintenance')}>
          Maintenance
        </Badge>
        <Badge variant={categoryFilter === 'front_desk' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setCategoryFilter(categoryFilter === 'front_desk' ? null : 'front_desk')}>
          Front Desk
        </Badge>
      </div>

      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold mb-2">No Requests</h3>
            <p className="text-sm text-muted-foreground">
              {filter === 'all' ? 'No service requests yet.' : `No ${filter} requests.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Guest</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRequests.map(request => {
                const isOverdue = request.escalated_to_manager;
                const createdAt = new Date(request.created_at);
                const ageMinutes = Math.floor((Date.now() - createdAt) / 1000 / 60);
                
                return (
                  <TableRow key={request.request_id} className={isOverdue ? 'bg-destructive/5' : ''} data-testid={`request-${request.request_id}`}>
                    <TableCell className="mono text-xs">{request.request_id.split('_')[2]}</TableCell>
                    <TableCell className="font-medium">{request.room_number}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {request.category === 'housekeeping' && 'Housekeeping'}
                        {request.category === 'maintenance' && 'Maintenance'}
                        {request.category === 'front_desk' && 'Front Desk'}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{request.issue}</TableCell>
                    <TableCell>
                      <PmsStatusBadge status={request.status} size="sm" />
                      {isOverdue && <Badge variant="destructive" className="ml-1 text-xs">OVERDUE</Badge>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={request.priority === 'urgent' ? 'destructive' : request.priority === 'high' ? 'warning' : 'outline'} className="text-xs">
                        {request.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {ageMinutes < 60 ? `${ageMinutes}m ago` : `${Math.floor(ageMinutes / 60)}h ago`}
                    </TableCell>
                    <TableCell className="text-sm">{request.guest_name || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
